// Meta Pixel (browser-only) — consent-gated install for the public WEB.
//
// Reuses the SINGLE existing consent system (advertising consent = granted when
// lib/consent.js has analytics:true). No second consent system. No CAPI, no
// server-side, no Automatic Advanced Matching, no PII sent to Meta. Does NOT touch
// UTM/fbclid capture, first/last touch, CRM attribution, or localStorage.
//
// Lifecycle:
//   advertising consent unknown/denied → pixel NOT loaded/initialized, 0 events.
//   advertising consent granted        → lazy-load fbevents.js, init once, track.
//   revoke                             → fbq('consent','revoke'); all our calls are
//                                        gated so no new events are sent.
import { adConsentState } from "./attribution";

export const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || "1143868544791034";

// Key paid/intent landings → ViewContent (non-PII content_name only).
const VIEWCONTENT_PATHS = {
  "/spain/home": "spain_home",
  "/spain/retire": "spain_retire",
  "/spain/moving-to-spain": "spain_moving_to_spain",
  "/retire-to-spain": "retire_to_spain",
  "/move-to-spain": "move_to_spain",
  "/digital-nomad-spain": "digital_nomad_spain",
};

let initialized = false;
let lastPageUrl = null;

const granted = () => adConsentState() === "granted";
const canTrack = () => granted() && initialized && typeof window.fbq === "function";

function loadScript() {
  if (window.fbq) return;
  const n = (window.fbq = function () {
    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
  });
  if (!window._fbq) window._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = "2.0";
  n.queue = [];
  const s = document.createElement("script");
  s.async = true;
  s.src = "https://connect.facebook.net/en_US/fbevents.js";
  const first = document.getElementsByTagName("script")[0];
  first.parentNode.insertBefore(s, first);
}

function ensureInit() {
  if (initialized) return;
  loadScript(); // 1. load fbevents.js
  window.fbq.disablePushState = true; // the router owns PageViews (no double)
  window.fbq("init", META_PIXEL_ID); // 2. init FIRST — no customer-data arg → no advanced matching
  window.fbq("set", "autoConfig", false, META_PIXEL_ID); // 3. config: no auto advanced-matching / auto events
  window.fbq("consent", "grant"); // 4. consent (ensureInit only runs when advertising consent is granted)
  initialized = true;
}

// Call on load and on every consent change. Init only when advertising granted;
// on denial/revoke stop sending new events.
export function syncMetaConsent() {
  if (granted()) {
    ensureInit();
  } else if (window.fbq) {
    try { window.fbq("consent", "revoke"); } catch { /* ignore */ }
    lastPageUrl = null;
  }
}

// One PageView (+ ViewContent on key landings) per distinct URL. Deduped so route
// effects, re-renders and the consent-grant handler never double-fire for one URL.
export function metaTrackPage(pathname, search = "") {
  if (!canTrack()) return;
  const url = (pathname || "/") + (search || "");
  if (url === lastPageUrl) return;
  lastPageUrl = url;
  window.fbq("track", "PageView");
  const key = (pathname || "").replace(/\/+$/, "") || "/";
  const name = VIEWCONTENT_PATHS[key];
  if (name) window.fbq("track", "ViewContent", { content_name: name, content_type: "landing_page" });
}

// Standard Lead event (non-PII). Caller must fire only after a successful backend
// response and guard once-per-conversion.
export function metaLead(contentName) {
  if (!canTrack()) return;
  window.fbq("track", "Lead", { content_name: contentName, content_category: "lead" });
}
